import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { getStaff, createStaffMember, updateStaffMember, deleteStaffMember, updateStaffStatus, updateStaffLimits, resetStaffLimits } from '../services/staffService';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';

const Staff = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const roleFilter = searchParams.get('role') || 'operator';
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: 'operator',
    telegram: '',
    status: 'active'
  });

  // Состояние для диалога настройки лимитов
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [limitsTarget, setLimitsTarget] = useState(null);
  const [limitsSaving, setLimitsSaving] = useState(false);
  const [limitsForm, setLimitsForm] = useState({
    viewAddresses: '',
    editAddresses: '',
    deleteAddresses: '',
    resetHour: 0
  });

  const isCurator = currentUser?.role === 'curator';

  useEffect(() => {
    fetchStaff();
  }, [location.search]);

  const fetchStaff = async () => {
    try {
      const response = await getStaff();
      const filteredStaff = response.data.filter(member => member.role === roleFilter);
      setStaff(filteredStaff);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setFormData({
      username: '',
      password: '',
      name: '',
      role: roleFilter,
      telegram: '',
      status: 'active'
    });
    setErrors({});
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
  };

  const handleEdit = (member) => {
    setEditMode(true);
    setFormData({
      _id: member._id,
      username: member.username,
      name: member.name,
      role: member.role,
      telegram: member.telegram || '',
      status: member.status
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setErrors({});
      if (editMode) {
        await updateStaffMember(formData._id, formData);
      } else {
        await createStaffMember(formData);
      }
      fetchStaff();
      handleClose();
    } catch (error) {
      console.error('Error saving staff member:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Ошибка при сохранении сотрудника';
      enqueueSnackbar(message, { variant: 'error' });
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.error && errorData.error.includes('validation failed')) {
          const validationErrors = {};
          const errorMessage = errorData.error;
          Object.keys(formData).forEach(field => {
            const fieldError = new RegExp(`${field}: (.*?)(?=(,|$))`).exec(errorMessage);
            if (fieldError) {
              validationErrors[field] = fieldError[1];
            }
          });
          setErrors(validationErrors);
        }
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого сотрудника?')) {
      try {
        await deleteStaffMember(id);
        fetchStaff();
      } catch (error) {
        console.error('Error deleting staff member:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateStaffStatus(id, { status: newStatus });
      fetchStaff();
    } catch (error) {
      console.error('Error updating staff status:', error);
    }
  };

  const handleOpenLimits = (member) => {
    setLimitsTarget(member);
    setLimitsForm({
      viewAddresses: member.limits?.viewAddresses ?? '',
      editAddresses: member.limits?.editAddresses ?? '',
      deleteAddresses: member.limits?.deleteAddresses ?? '',
      resetHour: member.limits?.resetHour ?? 0
    });
    setLimitsOpen(true);
  };

  const handleCloseLimits = () => {
    setLimitsOpen(false);
    setLimitsTarget(null);
  };

  const handleSaveLimits = async () => {
    if (!limitsTarget) return;

    setLimitsSaving(true);
    try {
      const payload = {
        viewAddresses: limitsForm.viewAddresses === '' ? null : Number(limitsForm.viewAddresses),
        editAddresses: limitsForm.editAddresses === '' ? null : Number(limitsForm.editAddresses),
        deleteAddresses: limitsForm.deleteAddresses === '' ? null : Number(limitsForm.deleteAddresses),
        resetHour: Number(limitsForm.resetHour)
      };
      await updateStaffLimits(limitsTarget._id, payload);
      enqueueSnackbar('Лимиты сохранены', { variant: 'success' });
      fetchStaff();
      handleCloseLimits();
    } catch (error) {
      console.error('Error saving limits:', error);
      const message = error?.response?.data?.error || error?.response?.data?.message || 'Ошибка при сохранении лимитов';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLimitsSaving(false);
    }
  };

  const handleResetLimits = async (member) => {
    if (!window.confirm(`Сбросить текущее использование лимитов оператора ${member.name || member.username}?`)) return;

    try {
      await resetStaffLimits(member._id);
      enqueueSnackbar('Лимиты сброшены', { variant: 'success' });
      fetchStaff();
    } catch (error) {
      console.error('Error resetting limits:', error);
      const message = error?.response?.data?.error || error?.response?.data?.message || 'Ошибка при сбросе лимитов';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">{roleFilter === 'operator' ? 'Операторы' : 'Курьеры'}</Typography>
        {hasPermission('staff', 'create') && (
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Добавить
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Логин</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Роль</TableCell>
              <TableCell>Telegram</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Лимиты (просм/ред/удал)</TableCell>
              <TableCell>Использовано (просм/ред/удал)</TableCell>
              <TableCell>Последняя активность</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((member) => (
              <TableRow key={member._id}>
                <TableCell>{member.username}</TableCell>
                <TableCell>{member.name}</TableCell>
                <TableCell>
                  {member.role === 'operator' ? 'Оператор' : 'Курьер'}
                </TableCell>
                <TableCell>{member.telegram || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={member.status === 'active' ? 'Активен' : 'Неактивен'}
                    color={member.status === 'active' ? 'success' : 'default'}
                    onClick={() => handleStatusChange(member._id, member.status === 'active' ? 'inactive' : 'active')}
                  />
                </TableCell>
                <TableCell>
                  {member.role === 'operator' ? (
                    member.limits?.viewAddresses != null || member.limits?.editAddresses != null || member.limits?.deleteAddresses != null ? (
                      <Chip
                        size="small"
                        label={`${member.limits.viewAddresses ?? '∞'}/${member.limits.editAddresses ?? '∞'}/${member.limits.deleteAddresses ?? '∞'}`}
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Chip size="small" label="Без лимита" variant="outlined" />
                    )
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {member.role === 'operator' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption">
                        Просмотр: <strong>{member.limitsUsage?.viewAddresses ?? 0}</strong>
                        {member.limits?.viewAddresses != null ? ` / ${member.limits.viewAddresses}` : ' / ∞'}
                      </Typography>
                      <Typography variant="caption">
                        Редактирование: <strong>{member.limitsUsage?.editAddresses ?? 0}</strong>
                        {member.limits?.editAddresses != null ? ` / ${member.limits.editAddresses}` : ' / ∞'}
                      </Typography>
                      <Typography variant="caption">
                        Удаление: <strong>{member.limitsUsage?.deleteAddresses ?? 0}</strong>
                        {member.limits?.deleteAddresses != null ? ` / ${member.limits.deleteAddresses}` : ' / ∞'}
                      </Typography>
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {member.lastActive ? new Date(member.lastActive).toLocaleString() : '-'}
                </TableCell>
                <TableCell align="right">
                  {isCurator && member.role === 'operator' && (
                    <>
                      <IconButton onClick={() => handleOpenLimits(member)} title="Настроить лимиты">
                        <TuneIcon />
                      </IconButton>
                      <IconButton onClick={() => handleResetLimits(member)} title="Сбросить лимиты" color="warning">
                        <RestartAltIcon />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleEdit(member)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(member._id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Редактировать сотрудника' : 'Добавить сотрудника'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              margin="dense"
              label="Логин"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={!!errors.username}
              helperText={errors.username}
            />
            {!editMode && (
              <TextField
                margin="dense"
                label="Пароль"
                type="password"
                fullWidth
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={!!errors.password}
                helperText={errors.password}
              />
            )}
            <TextField
              margin="dense"
              label="Имя"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
            <input type="hidden" value={formData.role} />
            <TextField
              margin="dense"
              label="Telegram"
              fullWidth
              value={formData.telegram}
              onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
              error={!!errors.telegram}
              helperText={errors.telegram}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Статус</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                error={!!errors.status}
              >
                <MenuItem value="active">Активен</MenuItem>
                <MenuItem value="inactive">Неактивен</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог настройки суточных лимитов оператора */}
      <Dialog open={limitsOpen} onClose={handleCloseLimits} maxWidth="sm" fullWidth>
        <DialogTitle>Настроить лимиты оператора</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {limitsTarget?.name || limitsTarget?.username}
            </Typography>
            <TextField
              margin="dense"
              label="Лимит на просмотр адресов / сутки"
              type="number"
              fullWidth
              value={limitsForm.viewAddresses}
              onChange={(e) => setLimitsForm({ ...limitsForm, viewAddresses: e.target.value })}
              helperText="Учитывается при открытии содержимого адреса. Пусто = без ограничения"
              InputProps={{ inputProps: { min: 0 } }}
            />
            <TextField
              margin="dense"
              label="Лимит на редактирование адресов / сутки"
              type="number"
              fullWidth
              value={limitsForm.editAddresses}
              onChange={(e) => setLimitsForm({ ...limitsForm, editAddresses: e.target.value })}
              helperText="Оставьте пустым, чтобы снять ограничение"
              InputProps={{ inputProps: { min: 0 } }}
            />
            <TextField
              margin="dense"
              label="Лимит на удаление адресов / сутки"
              type="number"
              fullWidth
              value={limitsForm.deleteAddresses}
              onChange={(e) => setLimitsForm({ ...limitsForm, deleteAddresses: e.target.value })}
              helperText="Оставьте пустым, чтобы снять ограничение"
              InputProps={{ inputProps: { min: 0 } }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Час сброса лимитов</InputLabel>
              <Select
                value={limitsForm.resetHour}
                onChange={(e) => setLimitsForm({ ...limitsForm, resetHour: e.target.value })}
                label="Час сброса лимитов"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Лимиты сбрасываются автоматически каждый день в указанный час.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLimits}>Отмена</Button>
          <Button onClick={handleSaveLimits} variant="contained" color="primary" disabled={limitsSaving}>
            {limitsSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Staff;
